'use client';

import { c as _c } from "react/compiler-runtime";
import { theme } from '@cc-shopping/ui/theme';
import { type BoxProps, Tooltip, type TooltipProps, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { forwardRef, useState } from 'react';
import { AddToCompareCheckbox } from './AddToCompareCheckbox';
import { useCompareBar } from './Context';
import type { FireCheckboxAnalyticsEventsType } from './types';
export type AddToCompareBarProps = Omit<Partial<BoxProps>, 'ref'> & {
  creditCardOfferId: string;
  fireAnalyticsEvents: FireCheckboxAnalyticsEventsType;
  variant: 'design1' | 'design2';
};
export interface NWTooltipProps extends TooltipProps {
  maxWidth?: string | number;
}
const MuiTooltip = forwardRef<HTMLDivElement | null, NWTooltipProps>((t0, ref) => {
  const $ = _c(17);
  let children;
  let className;
  let maxWidth;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      maxWidth,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = maxWidth;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    maxWidth = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== maxWidth) {
    t1 = {
      maxWidth
    };
    $[5] = maxWidth;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== className) {
    t2 = {
      tooltip: className
    };
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children) {
    t3 = <span>{children}</span>;
    $[9] = children;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== props || $[12] !== ref || $[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
    t4 = <Tooltip sx={t1} {...props} ref={ref} classes={t2}>{t3}</Tooltip>;
    $[11] = props;
    $[12] = ref;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
const CompareCheckboxTooltip = styled(MuiTooltip)(({
  theme
}) => {
  const {
    common
  } = theme.palette;
  const {
    fontFamily
  } = theme.typography;
  return {
    backgroundColor: common.black,
    borderColor: common.black,
    borderRadius: 4,
    boxShadow: 'none',
    color: common.white,
    fontFamily,
    fontSize: '11px',
    fontWeight: '325',
    lineHeight: '13.2px',
    padding: '10px',
    '& .MuiTooltip-arrow': {
      color: common.black
    },
    '& .MuiTooltip-arrow::before': {
      backgroundColor: common.black
    }
  };
});
export const AddToCompareCheckboxWithTooltip = t0 => {
  const $ = _c(21);
  let creditCardOfferId;
  let fireAnalyticsEvents;
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      creditCardOfferId,
      fireAnalyticsEvents,
      variant,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = creditCardOfferId;
    $[2] = fireAnalyticsEvents;
    $[3] = props;
    $[4] = variant;
  } else {
    creditCardOfferId = $[1];
    fireAnalyticsEvents = $[2];
    props = $[3];
    variant = $[4];
  }
  const {
    idsToCompare
  } = useCompareBar();
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = theme.breakpoints.down("md");
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const isMobile = useMediaQuery(t1);
  let t2;
  if ($[6] !== creditCardOfferId || $[7] !== idsToCompare) {
    t2 = idsToCompare.includes(creditCardOfferId);
    $[6] = creditCardOfferId;
    $[7] = idsToCompare;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const addedToCompare = t2;
  const disableCheckbox = isMobile ? idsToCompare.length >= 2 : idsToCompare.length === 3;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  let t3;
  if ($[9] !== isTooltipOpen) {
    t3 = () => {
      setIsTooltipOpen(!isTooltipOpen);
    };
    $[9] = isTooltipOpen;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleTouchStart = t3;
  let t4;
  if ($[11] !== addedToCompare || $[12] !== creditCardOfferId || $[13] !== disableCheckbox || $[14] !== fireAnalyticsEvents || $[15] !== handleTouchStart || $[16] !== isMobile || $[17] !== isTooltipOpen || $[18] !== props || $[19] !== variant) {
    t4 = !addedToCompare && disableCheckbox ? <CompareCheckboxTooltip arrow={true} maxWidth={203} title={`You can select a maximum of ${isMobile ? 2 : 3} cards`} {...isMobile ? {
      open: isTooltipOpen
    } : {}}><AddToCompareCheckbox creditCardOfferId={creditCardOfferId} fireAnalyticsEvents={fireAnalyticsEvents} handleTouchStart={handleTouchStart} variant={variant} {...props} /></CompareCheckboxTooltip> : <AddToCompareCheckbox creditCardOfferId={creditCardOfferId} fireAnalyticsEvents={fireAnalyticsEvents} variant={variant} {...props} />;
    $[11] = addedToCompare;
    $[12] = creditCardOfferId;
    $[13] = disableCheckbox;
    $[14] = fireAnalyticsEvents;
    $[15] = handleTouchStart;
    $[16] = isMobile;
    $[17] = isTooltipOpen;
    $[18] = props;
    $[19] = variant;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
};